/**
 * Lifetime Page Store — caches metadata and wetness samples across navigation
 * so going Inspector → Lifetime doesn't trigger a full refetch.
 */

import { create } from 'zustand';
import { BackendMeta, WetnessSample, getAllClipMeta, getWetnessSamplesForClips } from '../lib/api-clip-monitor';

interface LifetimeStore {
  allMetas: Record<string, BackendMeta>;
  allWetnessSamples: Record<string, WetnessSample[]>;
  loadingMetas: boolean;
  loadingWetness: boolean;

  /** Key tracking which source+bucket the cached metas belong to */
  metasCacheKey: string | null;
  /** Key tracking which clips the cached wetness belongs to */
  wetnessCacheKey: string | null;

  viewMode: 'site' | 'clip';
  selectedSite: string | null;
  sortCol: 'start' | 'clip' | 'duration' | 'volume';
  sortDesc: boolean;

  setViewMode: (mode: 'site' | 'clip') => void;
  setSelectedSite: (site: string | null) => void;
  setSortCol: (col: 'start' | 'clip' | 'duration' | 'volume') => void;
  setSortDesc: (desc: boolean) => void;

  fetchMetas: (cacheKey: string) => Promise<void>;
  fetchWetness: (clipAddresses: string[]) => Promise<void>;
  clearWetness: () => void;
}

export const useLifetimeStore = create<LifetimeStore>((set, get) => ({
  allMetas: {},
  allWetnessSamples: {},
  loadingMetas: false,
  loadingWetness: false,
  metasCacheKey: null,
  wetnessCacheKey: null,

  viewMode: 'site',
  selectedSite: null,
  sortCol: 'start',
  sortDesc: true,

  setViewMode: (mode) => set({ viewMode: mode }),
  setSelectedSite: (site) => set({ selectedSite: site }),
  setSortCol: (col) => set({ sortCol: col }),
  setSortDesc: (desc) => set({ sortDesc: desc }),

  fetchMetas: async (cacheKey: string) => {
    if (get().metasCacheKey === cacheKey && Object.keys(get().allMetas).length > 0) return;

    set({ loadingMetas: true });
    try {
      const metas = await getAllClipMeta();
      const sites = Array.from(new Set(Object.values(metas).map(m => m.site_name).filter(Boolean))).sort();
      const prevSite = get().selectedSite;
      const selectedSite = (prevSite && sites.includes(prevSite)) ? prevSite : (sites[0] || null);

      set({ allMetas: metas, metasCacheKey: cacheKey, loadingMetas: false, selectedSite });
    } catch {
      set({ loadingMetas: false });
    }
  },

  fetchWetness: async (clipAddresses: string[]) => {
    const key = clipAddresses.sort().join(',');
    if (get().wetnessCacheKey === key && Object.keys(get().allWetnessSamples).length > 0) return;

    set({ loadingWetness: true, allWetnessSamples: {} });
    try {
      const samples = await getWetnessSamplesForClips(clipAddresses, 300);
      set({ allWetnessSamples: samples, wetnessCacheKey: key, loadingWetness: false });
    } catch {
      set({ loadingWetness: false });
    }
  },

  clearWetness: () => set({ allWetnessSamples: {}, wetnessCacheKey: null }),
}));
